import { Jaco } from "./Jaco";
import { ModelGhost } from "./ModelGhost";
import { ModelSizeCutted } from "./ModelSizeCutted";
import * as Scenes from "oc-threejs/Scenes";
import * as Commons from "oc-commons/Commons";

import type { Model } from "./Model";
import { Group, Object3D } from "three";

// APP interface extension
interface IApp {
  arrangement: Jaco;
}
declare const APP: IApp;

interface IModelParameters {
  modelMaterialRelation: any;
  clippingParameters?: any;
  [key: string]: any;
}

interface IModelShell {
  model: Model; // Or ModelSizeCutted
  meta: any;
  ghosts: ModelGhost[];
}

export class ModelGhostPool {
  modelShells: IModelShell[] = [];
  startCounter: number = 0;
  arrangement: Jaco | null = null;
  child3dContainer: Group;

  constructor(arrangement: Jaco) {
    this.arrangement = arrangement;
    this.child3dContainer = new Group();
    this.child3dContainer.name = "ModelGhostPool";
    this.child3dContainer.visible = false;
  }

  modelCreator(modelParameters: IModelParameters): Model {
    const model = new ModelSizeCutted(this as any, modelParameters.modelMaterialRelation) as Model;
    Object.assign(model, modelParameters);
    return model;
  }

  defaultModelParameters() {
    return {
      clippingParameters: null,
    };
  }

  isEqual(model: Model, modelParameters: IModelParameters) {
    return (
      model.modelMaterialRelation === modelParameters.modelMaterialRelation &&
      Commons.isEqualEpsilon(model.clippingParameters, modelParameters.clippingParameters)
    );
  }

  createGhost() {
    // ModelGhost constructor requires parent and model, but here it seems we are just creating an empty one
    // or the constructor signature in ModelGhost.ts is constructor(parent, model).
    // In ModelGhost.ts I defined constructor(parent: Jaco | null, model: Model).
    // The original JS `createGhost` called `new ModelGhost()`.
    // This means arguments were undefined.
    // Strict TS will complain.
    // I should check `ModelGhost.ts` again. Step 191 defines specific args.
    // But in JS arguments can be missing.
    // I will adjust calling to pass null/undefined or fix ModelGhost to allow optional args.
    // However, `get` method assigns `ghost.model = modelShell.model`.
    // So creating with casting to any or changing ModelGhost signature is needed.
    // I'll call it with (null as any, null as any) to satisfy TS, then assign.
    return new ModelGhost(null, null as any);
  }

  /**
   * löscht Instanzen aus dem Pool - muss auf jeder Jaco-Ebene am Anfang von update3d
   * aufgerufen werden für alle Arrays von mit diesem Pool erzeugten Modellen
   * @param models
   */
  clearInstances(models: any[]) {
    if (models) {
      if (!Array.isArray(models)) {
        throw new Error("use clearInstance for clearing non arrays");
      }
      models.forEach((model) => this.clearInstance(model));
    }
    return [];
  }

  /**
   * löscht Instanzen aus dem Pool - muss auf jeder Jaco-Ebene am Anfang von update3d
   * aufgerufen werden für alle Arrays von mit diesem Pool erzeugten Modellen
   * @param ghost
   */
  clearInstance(ghost: any): null {
    if (ghost) {
      if (Array.isArray(ghost)) {
        throw new Error("use clearInstances for clearing arrays");
      }
      if (!(ghost instanceof ModelGhost)) {
        throw new Error("can only clear ModelGhosts, but is " + Commons.typeName(ghost));
      }
      ghost._used = false;
      ghost.parent = null;
    }
    return null;
  }

  getForModelMaterialRelation(parent: Jaco, modelMaterialRelation: any, clippingParameters: any) {
    return this.get(parent, {
      modelMaterialRelation,
      clippingParameters: {
        ...clippingParameters,
      },
    });
  }

  /**
   * gibt einen ModelGhost zurück (also eine leichtgewichtige Kopie des Objektes).
   * update3d muss auf dem zurückgegebenen Objekt aufgerufen werden.
   * @param {Jaco} parent
   * @param {Object} modelParameters
   * @return {ModelGhost}
   */
  get(parent: Jaco, modelParameters: IModelParameters): ModelGhost {
    if (this.startCounter == 0) {
      debugger;
      throw new Error(
        "ModelGhostPool.get() muss zwischen .start() und .cleanup() aufgerufen werden",
      );
    }

    // gibt es ein den Parametern entsprechendes Model? Dann gib es zurück, sonst erstelle eines
    let modelShell = this.modelShells.find(({ model }) => this.isEqual(model, modelParameters));
    if (modelShell == null) {
      const model = this.modelCreator(modelParameters);
      modelShell = {
        model,
        meta: model.getMeta(),
        ghosts: [],
      };
      if (
        !Commons.isEqualEpsilon(
          modelShell.model.clippingParameters,
          modelParameters.clippingParameters,
        )
      ) {
        debugger;
        throw new Error(
          "die clippingParameters, nach denen gesucht wird, müssen denen des Models entsprechen",
        );
      }
      this.modelShells.push(modelShell);
    }

    // nimm einen unbenutzen Ghost oder erstelle einen neuen
    let ghost = modelShell.ghosts.find((ghost) => !ghost._used);
    if (!ghost) {
      ghost = this.createGhost();
      modelShell.ghosts.push(ghost);
    }
    ghost.model = modelShell.model;
    (ghost as any)._modelShell = modelShell; // _modelShell not defined in ModelGhost
    ghost.parent = parent;
    ghost._used = true;

    // um mehrfahce Verwendung aufzuspüren nützlich
    // APP.arrangement.traverse((o) => {
    //   if (o.id == ghost.id) {
    //     debugger;
    //   }
    // });

    return ghost;
  }

  /**
   * funktionale Kurzschreibweise für einen start / cleanup - Block.
   * withPoolClean sollte innerhalb eines Arrangement.update3d aufgerufen werden, und zwar genau ein Mal!
   * @param callback
   * @returns {*}
   */
  withPoolClean<T>(callback: () => T): T {
    this.start();
    try {
      return callback();
    } finally {
      this.cleanup();
    }
  }

  /**
   * withPool leert die Referenzzählung nicht, und räumt auch keine überflüssigen Objekte ab.
   * withPool sollte ausserhalb eines Arrangement.update3d verwendet werden statt withPoolClean
   * @param callback
   * @returns {*}
   */
  withPool<T>(callback: () => T): T | undefined {
    this.startCounter++;
    if (this.startCounter > 1) {
      return;
    }
    try {
      return callback();
    } finally {
      this.startCounter--;
      if (this.startCounter > 0) {
        // Did not return early here in JS logically?
        // JS: if (this.startCounter > 0) return;
        // Logic seems to imply re-entrant protection.
        // It returns undefined in "finally"? No.
        // The return is for the `withPool` function.
      }
    }
    // TS complains if not all paths return value if return type is T.
    // JS returns result of callback.
    return undefined; // Implicit return undefined in JS if guards hit
  }

  /**
   * Jede Verwendung des Pools muss in einer start() / cleanup() Klammer stehen
   */
  start() {
    this.startCounter++;
    if (this.startCounter > 1) {
      return;
    }
    if (this.child3dContainer.parent == null && this.arrangement && this.arrangement.jaco3d) {
      if (this.arrangement.jaco3d instanceof Object3D) {
        this.arrangement.jaco3d.add(this.child3dContainer);
      } else {
        // Jaco3d is mixin, acts as Object3D
        (this.arrangement.jaco3d as any).add(this.child3dContainer);
      }
    }
  }

  cleanup() {
    this.startCounter--;
    if (this.startCounter > 0) {
      return;
    }

    const removeJaco3d = (ghost: ModelGhost | Model) => {
      if (ghost && ghost.jaco3d) {
        Scenes.remove(ghost.jaco3d);
        ghost.jaco3d = null;
        if ((ghost as any)._modelShell) (ghost as any)._modelShell = null;
      }
    };

    const usedSet = new Set<Jaco>();
    const multiples = new Set<Jaco>();
    if (this.arrangement) {
      this.arrangement.traverse((o) => {
        if (usedSet.has(o) && (o as any).isGhost) {
          multiples.add(o);
        }
        usedSet.add(o);
      });
    }

    if (multiples.size > 0) {
      console.warn(
        "arrangement.traverse() visits some Ghosts more than once, which is a sign that they are children of more than one parent, which is a failure",
        Array.from(multiples),
      );
    }

    const used = (o: ModelGhost) => o._used && usedSet.has(o);
    this.modelShells.forEach((modelShell) => {
      const [usedGhosts, unusedGhosts] = Commons.partition(modelShell.ghosts, used);

      unusedGhosts.forEach(removeJaco3d);

      modelShell.ghosts = usedGhosts;
    });

    const [shellsWithoutReferences, usedShells] = Commons.partition(
      this.modelShells,
      (model) => model.ghosts.length == 0,
    );
    shellsWithoutReferences.forEach((modelShell) => {
      modelShell.ghosts.forEach(removeJaco3d);
      removeJaco3d(modelShell.model);
    });
    this.modelShells = usedShells;
  }

  traverse(iterator: (model: Model) => void) {
    this.modelShells.forEach((modelShell) => iterator(modelShell.model));
  }
}
